import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { UpsellingBanner as F1ProductBanner } from '@factorialco/f0-react'
import { Upsell } from '@factorialco/f0-react/icons/app'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useMarketplaceModulesPath } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'
import { ProductModal } from 'modules/upselling_kit/components/ProductModal'
import { useGetUpsellingData } from 'modules/upselling_kit/hooks/useGetUpsellingData'
import { UpsellingKitProductBanner } from 'modules/upselling_kit/types'
import {
  buildUpsellOriginParam,
  handleUpsellingLearnMore,
  handleUpsellingOnClose,
  handleUpsellingVisibility,
} from 'modules/upselling_kit/utils/handlers'

const ProductBannerContent = ({
  title,
  subtitle,
  mediaUrl,
  onClose,
  learnMoreNavigate,
  primaryAction,
  secondaryAction,
  ...upsellProps
}: UpsellingKitProductBanner) => {
  const { moduleRequested, requestSource, requestReason, featureRequested } = upsellProps
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { moduleSlug } = useGetUpsellingData({
    moduleRequested,
  })
  const navigate = useNavigate()
  const { getDetailPath } = useMarketplaceModulesPath()

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleLearnMore = () => {
    handleUpsellingLearnMore({
      moduleRequested,
      requestSource,
      component: 'product_banner',
    })
    const upsellOriginParam = buildUpsellOriginParam(
      'product_banner',
      requestSource,
      'Upselling Kit'
    )
    if (learnMoreNavigate) {
      const separator = learnMoreNavigate.includes('?') ? '&' : '?'
      navigate(`${learnMoreNavigate}${separator}${upsellOriginParam}`)
    } else {
      navigate(getDetailPath(moduleSlug || '', upsellOriginParam))
    }
  }

  const handleOnClose = () => {
    handleUpsellingOnClose({
      onClose,
      requestSource,
      moduleRequested,
      component: 'product_banner',
    })
  }

  useEffect(() => {
    handleUpsellingVisibility({
      moduleRequested,
      requestSource,
      component: 'product_banner',
    })
  }, [moduleRequested, requestSource])

  return (
    <>
      <F1ProductBanner
        title={title}
        subtitle={subtitle}
        mediaUrl={mediaUrl}
        onClose={handleOnClose}
        primaryAction={{
          label: primaryAction.label ?? i18n.t('upselling_kit.button.request_information'),
          onClick: () => {
            setIsModalOpen(true)
            track(trackingEvents.upsellingKit.clickProductUpsellComponent, {
              source: requestSource,
              module: moduleRequested,
              component: 'product_banner',
            })
          },
          icon: Upsell,
          variant:
            primaryAction.variant === 'promote' || primaryAction.variant === 'default'
              ? 'outline'
              : (primaryAction.variant ?? 'outline'),
        }}
        {...(secondaryAction && {
          secondaryAction: {
            label: secondaryAction.label ?? i18n.t('upselling_kit.button.learn_more'),
            onClick: handleLearnMore,
            variant:
              secondaryAction.variant === 'promote'
                ? 'default'
                : (secondaryAction.variant ?? 'default'),
          },
        })}
      />
      {isModalOpen && (
        <ProductModal
          moduleRequested={moduleRequested}
          requestSource={`${requestSource}`}
          requestReason={requestReason}
          featureRequested={featureRequested}
          pageSource={requestSource}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          openFromUpsellButton={false}
          learnMoreNavigate={learnMoreNavigate}
          fallbackComponent={null}
          upsellRequestComponentType="product_banner"
        />
      )}
    </>
  )
}

export default ProductBannerContent
